import React, { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import PostAddRoundedIcon from '@mui/icons-material/PostAddRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { hideEmail } from 'utils/user'
import { bulkCreateContractors } from 'services/contractorService'
import SearchableSelect from 'components/SearchableSelect'

const FIELD_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'company', label: 'Company' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'trade', label: 'Trade / service' },
  { value: 'vetting', label: 'Approval status' },
  { value: 'notes', label: 'Add to notes' },
  { value: 'ignore', label: 'Ignore' },
]

const isEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)

const guessField = header => {
  const h = header.trim().toLowerCase()
  if (/contractor name|supplier name|^name$/.test(h)) return 'name'
  if (/^company$|company name|trading name|business name/.test(h)) return 'company'
  if (/e-?mail/.test(h)) return 'email'
  if (/phone|telephone|\btel\b|mobile/.test(h)) return 'phone'
  if (/service|trade|discipline|specialism|type of work|works provided/.test(h)) return 'trade'
  if (/approval status|vetting|^status$/.test(h)) return 'vetting'
  return 'notes'
}

const mapVetting = value => {
  const v = value.trim().toLowerCase()
  if (!v) return null
  if (/approv/.test(v)) return 'approved'
  if (/probation/.test(v)) return 'probationary'
  if (/reject|suspend|declin/.test(v)) return 'suspended'
  return 'pending_review'
}

const splitLine = (line, delim) => {
  if (delim === '\t') return line.split('\t')
  const out = []
  let cur = ''
  let quoted = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++ }
      else if (ch === '"') quoted = false
      else cur += ch
    } else if (ch === '"') quoted = true
    else if (ch === ',') { out.push(cur); cur = '' }
    else cur += ch
  }
  out.push(cur)
  return out
}

const parse = (raw, firstRowIsHeader) => {
  const lines = raw.replace(/\r\n?/g, '\n').split('\n').filter(l => l.trim() !== '')
  if (lines.length === 0) return { headers: [], rows: [] }
  const delim = lines.some(l => l.includes('\t')) ? '\t' : ','
  const matrix = lines.map(l => splitLine(l, delim).map(c => c.trim()))
  const width = matrix.reduce((m, r) => Math.max(m, r.length), 0)
  if (firstRowIsHeader) {
    const headers = matrix[0]
    while (headers.length < width) headers.push(`Column ${headers.length + 1}`)
    return { headers, rows: matrix.slice(1) }
  }
  const headers = Array.from({ length: width }, (_, i) => (i === 0 ? 'Name' : `Column ${i + 1}`))
  return { headers, rows: matrix }
}

const BulkAddContractorsModal = ({ open, onClose, onAdded }) => {
  const [raw, setRaw] = useState('')
  const [firstRowIsHeader, setFirstRowIsHeader] = useState(true)
  const [mapping, setMapping] = useState(null)
  const [creating, setCreating] = useState(false)

  const { headers, rows } = useMemo(() => parse(raw, firstRowIsHeader), [raw, firstRowIsHeader])

  const effectiveMapping = useMemo(() => {
    if (mapping && mapping.length === headers.length) return mapping
    const guessed = headers.map(guessField)
    if (!guessed.includes('name')) {
      const idx = guessed.findIndex(g => g === 'notes')
      if (idx >= 0) guessed[idx] = 'name'
    }
    return guessed
  }, [headers, mapping])

  const setColumn = (i, value) => {
    const next = [...effectiveMapping]
    next[i] = value
    setMapping(next)
  }

  const contractors = useMemo(() => {
    if (headers.length === 0) return []
    return rows.map(cells => {
      const c = { name: '', company: null, email: null, phone: null, trade: null, vetting_status: null }
      const noteLines = []
      headers.forEach((h, i) => {
        const field = effectiveMapping[i]
        const val = (cells[i] ?? '').trim()
        if (!val || field === 'ignore') return
        const label = h.trim()
        if (field === 'notes') noteLines.push(`${label}: ${val}`)
        else if (field === 'name') { if (!c.name) c.name = val; else noteLines.push(`${label}: ${val}`) }
        else if (field === 'company') { if (!c.company) c.company = val }
        else if (field === 'phone') { if (!c.phone) c.phone = val }
        else if (field === 'trade') { if (!c.trade) c.trade = val }
        else if (field === 'vetting') { if (!c.vetting_status) c.vetting_status = mapVetting(val) }
        else if (field === 'email') {
          if (isEmail(val)) { if (!c.email) c.email = val }
          else noteLines.push(`${label}: ${val}`)
        }
      })
      c.notes = noteLines.length ? noteLines.join('\n') : null
      return c
    }).filter(c => c.name !== '')
  }, [headers, rows, effectiveMapping])

  const hasNameColumn = effectiveMapping.includes('name')
  const ready = hasNameColumn ? contractors.length : 0

  const close = () => { setRaw(''); setMapping(null); setFirstRowIsHeader(true); setCreating(false); onClose() }

  const submit = async () => {
    if (ready === 0 || creating) return
    setCreating(true)
    try {
      const res = await bulkCreateContractors(contractors)
      const count = res?.data?.data?.created ?? contractors.length
      toast.success(`Added ${count} contractor${count === 1 ? '' : 's'}.`)
      onAdded?.()
      close()
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not add those contractors. Please check the data and try again.'
      toast.error(msg)
      setCreating(false)
    }
  }

  if (!open) return null

  const previewChips = c => [
    c.company, c.trade, c.email ? hideEmail(c.email) : null, c.phone,
    c.vetting_status ? VETTING_LABEL[c.vetting_status] : null,
  ].filter(Boolean)

  return createPortal(
    <Backdrop onClick={close}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <ModalIcon><PostAddRoundedIcon style={{ fontSize: 20 }} /></ModalIcon>
          <ModalHeadText>
            <ModalTitle>Bulk add contractors</ModalTitle>
            <ModalSub>Paste your list from Excel or Google Sheets. We map the columns we recognise; everything else is kept in each contractor’s notes.</ModalSub>
          </ModalHeadText>
          <CloseBtn onClick={close} aria-label='Close'><CloseRoundedIcon style={{ fontSize: 18 }} /></CloseBtn>
        </ModalHead>

        <ModalBody>
          <Label>Paste your contractors</Label>
          <PasteArea
            placeholder={'Copy the rows from your spreadsheet (including the header row) and paste here.\n\nContractor Name\tPhone\tEmail\tServices Provided\tApproval Status\nPied Piper\t02085237666\tinfo@piedpiper.co.uk\tPest Control\tApproved'}
            value={raw}
            onChange={e => setRaw(e.target.value)}
          />
          <HeaderToggle onClick={() => setFirstRowIsHeader(v => !v)}>
            <ToggleBox $on={firstRowIsHeader}>{firstRowIsHeader && '✓'}</ToggleBox>
            The first row is a header row (column titles)
          </HeaderToggle>

          {headers.length > 0 && (
            <>
              <SectionLabel>Columns ({headers.length}): check where each one goes</SectionLabel>
              <ColsGrid>
                {headers.map((h, i) => (
                  <ColRow key={`${h}-${i}`}>
                    <ColName title={h}>{h || `Column ${i + 1}`}</ColName>
                    <div style={{ flex: 'none', width: 160 }}>
                      <SearchableSelect
                        value={effectiveMapping[i]}
                        onChange={v => setColumn(i, v)}
                        options={FIELD_OPTIONS}
                        ariaLabel={`Where does column ${h || `Column ${i + 1}`} go?`}
                      />
                    </div>
                  </ColRow>
                ))}
              </ColsGrid>

              {!hasNameColumn && (
                <Warn>Choose which column is the contractor’s <strong>name</strong>: it’s the one required field.</Warn>
              )}

              {ready > 0 && (
                <>
                  <SectionLabel>Preview: {ready} contractor{ready === 1 ? '' : 's'} ready</SectionLabel>
                  {contractors.slice(0, 5).map((c, i) => (
                    <PreviewCard key={i}>
                      <PName>{c.name}</PName>
                      {previewChips(c).length > 0 && (
                        <ChipRow>{previewChips(c).map((t, j) => <Chip key={j}>{t}</Chip>)}</ChipRow>
                      )}
                      {c.notes && <NotesBlock>{c.notes}</NotesBlock>}
                    </PreviewCard>
                  ))}
                  {ready > 5 && <MoreLine>+ {ready - 5} more</MoreLine>}
                </>
              )}
            </>
          )}
        </ModalBody>

        <ModalFoot>
          <FootNote>{ready === 0 ? 'Paste your list to get started.' : `${ready} ready to add`}</FootNote>
          <PrimaryBtn disabled={ready === 0 || creating} onClick={submit}>
            {creating ? 'Adding…' : ready > 0 ? `Add ${ready} contractor${ready === 1 ? '' : 's'}` : 'Add contractors'}
          </PrimaryBtn>
        </ModalFoot>
      </ModalCard>
    </Backdrop>,
    document.body
  )
}

export default BulkAddContractorsModal

const VETTING_LABEL = {
  pending_review: 'Pending review',
  approved: 'Approved',
  probationary: 'Probationary',
  suspended: 'Suspended',
}

const modalIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 1300;
  background: rgba(14, 20, 38, 0.42);
  display: flex; align-items: center; justify-content: center; padding: 24px;
  @media (max-width: 560px) { align-items: flex-end; padding: 0; }
`
const ModalCard = styled.div`
  background: #fff; border: 1px solid #eceef2; border-radius: 20px;
  width: min(640px, 100%); max-height: calc(100vh - 48px);
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 16px 44px rgba(12, 18, 41, 0.16);
  animation: ${modalIn} 200ms ease both;
  @media (max-width: 560px) { border-radius: 20px 20px 0 0; max-height: 92vh; }
`
const ModalHead = styled.header`
  display: flex; align-items: flex-start; gap: 12px;
  padding: 20px 20px 16px; border-bottom: 1px solid #f1f2f5;
`
const ModalIcon = styled.div`
  flex: none; width: 38px; height: 38px; border-radius: 11px;
  background: #e9f6f7; color: #1f8b98;
  display: flex; align-items: center; justify-content: center;
`
const ModalHeadText = styled.div`flex: 1; min-width: 0;`
const ModalTitle = styled.h2`margin: 0; font-size: 16px; font-weight: 800; color: #14171f;`
const ModalSub = styled.p`margin: 3px 0 0; font-size: 12.5px; line-height: 1.5; color: #6a7280;`
const CloseBtn = styled.button`
  flex: none; border: none; background: #f3f4f7; color: #6a7280;
  width: 30px; height: 30px; border-radius: 9px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: #e9ebf0; }
`
const ModalBody = styled.div`padding: 16px 20px; overflow-y: auto; flex: 1;`
const Label = styled.div`font-size: 12.5px; font-weight: 800; color: #33384a; margin-bottom: 7px;`
const SectionLabel = styled.div`font-size: 11.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.03em; color: #8a90a0; margin: 18px 0 9px;`
const PasteArea = styled.textarea`
  width: 100%; min-height: 128px; resize: vertical; font: inherit; font-size: 12.5px;
  line-height: 1.5; color: #1c2029; padding: 11px 13px; border: 1px solid #e2e5ea;
  border-radius: 12px; outline: none; box-sizing: border-box;
  &::placeholder { color: #b6bbc6; }
  &:focus { border-color: #2aa6b3; }
`
const HeaderToggle = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 8px; margin-top: 10px;
  background: none; border: none; cursor: pointer; font-size: 12.5px; color: #55606f; padding: 0;
`
const ToggleBox = styled.span`
  width: 18px; height: 18px; border-radius: 5px; display: inline-flex; align-items: center; justify-content: center;
  font-size: 12px; color: #fff;
  border: 1.5px solid ${({ $on }) => ($on ? '#2aa6b3' : '#cfd4dd')};
  background: ${({ $on }) => ($on ? '#2aa6b3' : '#fff')};
`
const ColsGrid = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 8px; @media (max-width: 520px) { grid-template-columns: 1fr; }`
const ColRow = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 7px 10px;
  border: 1px solid #eceef2; border-radius: 10px; background: #fbfcfd;
`
const ColName = styled.div`flex: 1; min-width: 0; font-size: 12.5px; font-weight: 600; color: #2a2f3a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`
const Warn = styled.div`
  margin-top: 12px; padding: 10px 13px; border-radius: 10px;
  background: #fef3c7; border: 1px solid #fde68a; color: #92620a; font-size: 12.5px; line-height: 1.5;
`
const PreviewCard = styled.div`border: 1px solid #eceef2; border-radius: 12px; padding: 11px 13px; margin-bottom: 9px;`
const PName = styled.div`font-size: 13.5px; font-weight: 800; color: #1c2029;`
const ChipRow = styled.div`display: flex; flex-wrap: wrap; gap: 6px; margin-top: 7px;`
const Chip = styled.span`font-size: 11.5px; font-weight: 600; color: #47505f; background: #f2f4f7; border-radius: 999px; padding: 3px 9px;`
const NotesBlock = styled.pre`
  margin: 9px 0 0; padding: 9px 11px; border-radius: 9px; background: #f6f8fa; border: 1px solid #eceef2;
  font: inherit; font-size: 11.5px; line-height: 1.5; color: #55606f; white-space: pre-wrap; word-break: break-word;
`
const MoreLine = styled.div`font-size: 12px; color: #8a90a0; padding: 2px 2px 4px;`
const ModalFoot = styled.footer`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 14px 20px; border-top: 1px solid #f1f2f5;
`
const FootNote = styled.span`font-size: 12px; color: #7a8090;`
const PrimaryBtn = styled.button`
  font: inherit; padding: 10px 18px; border: none; border-radius: 999px;
  background: #2aa6b3; color: #fff; font-size: 12.5px; font-weight: 800; cursor: pointer;
  transition: background 150ms ease;
  &:hover:not(:disabled) { background: #239aa6; }
  &:disabled { background: #e7e9ee; color: #a9adb7; cursor: not-allowed; }
`
